import React from 'react';
import { 
  Skeleton, 
  Card, 
  CardContent, 
  Box, 
  Grid,
  Paper,
  List,
  ListItem,
  Avatar
} from '@mui/material';

// Dashboard skeleton loader
export const DashboardSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ mb: 4 }}>
      <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
      <Skeleton variant="text" width={150} height={24} />
    </Box>

    {/* Stats Cards */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {[1, 2, 3, 4].map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={60} height={32} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Chart Area */}
    <Paper sx={{ p: 3, mb: 4 }}>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" width="100%" height={300} />
    </Paper>

    {/* Recent Activity */}
    <Paper sx={{ p: 3 }}>
      <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        </Box>
      ))}
    </Paper>
  </Box>
);

// People page skeleton loader
export const PeopleSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
      <Box>
        <Skeleton variant="text" width={150} height={40} />
        <Skeleton variant="text" width={100} height={24} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="rectangular" width={120} height={40} />
        <Skeleton variant="rectangular" width={100} height={40} />
      </Box>
    </Box>

    {/* Search and Filters */}
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Skeleton variant="rectangular" width={300} height={56} />
      <Skeleton variant="rectangular" width={200} height={56} />
      <Skeleton variant="rectangular" width={150} height={56} />
    </Box>

    {/* People Cards */}
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" width={60} height={24} />
                  <Skeleton variant="rectangular" width={60} height={24} />
                  <Skeleton variant="rectangular" width={60} height={24} />
                </Box>
              </Box>
              <Skeleton variant="rectangular" width="100%" height={36} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Timeline skeleton loader
export const TimelineSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
      <Skeleton variant="text" width={200} height={40} />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="rectangular" width={100} height={40} />
        <Skeleton variant="rectangular" width={100} height={40} />
      </Box>
    </Box>

    {/* Controls */}
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Skeleton variant="rectangular" width={120} height={40} />
      <Skeleton variant="rectangular" width={120} height={40} />
      <Skeleton variant="rectangular" width={150} height={40} />
      <Skeleton variant="rectangular" width={150} height={40} />
    </Box>

    {/* Timeline Area */}
    <Paper sx={{ p: 3 }}>
      <Skeleton variant="rectangular" width="100%" height={500} />
    </Paper>
  </Box>
);

// Individual Profile skeleton loader
export const IndividualSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
      <Skeleton variant="circular" width={80} height={80} sx={{ mr: 3 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={150} height={20} />
      </Box>
      <Skeleton variant="rectangular" width={120} height={40} />
    </Box>

    {/* Stats */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {[1, 2, 3, 4].map((item) => (
        <Grid item xs={12} sm={6} md={3} key={item}>
          <Paper sx={{ p: 2 }}>
            <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={60} height={32} />
          </Paper>
        </Grid>
      ))}
    </Grid>

    {/* Forms Table */}
    <Paper sx={{ p: 3 }}>
      <Skeleton variant="text" width={150} height={32} sx={{ mb: 3 }} />
      
      {/* Table Header */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
        <Skeleton variant="text" width={200} height={24} />
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={100} height={24} />
        <Skeleton variant="text" width={100} height={24} />
      </Box>
      
      {/* Table Rows */}
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <Box key={item} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Skeleton variant="text" width={200} height={20} />
          <Skeleton variant="rectangular" width={80} height={24} />
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="rectangular" width={100} height={32} />
        </Box>
      ))}
    </Paper>
  </Box>
);

// Add User skeleton loader
export const AddUserSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
    <Skeleton variant="text" width={200} height={40} sx={{ mb: 4 }} />
    
    <Paper sx={{ p: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Skeleton variant="rectangular" width={100} height={40} />
            <Skeleton variant="rectangular" width={100} height={40} />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

// Generic List skeleton
export const ListSkeleton = ({ items = 8 }) => (
  <List>
    {Array.from({ length: items }).map((_, index) => (
      <ListItem key={index}>
        <Avatar sx={{ mr: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      </ListItem>
    ))}
  </List>
);

// Generic Card skeleton
export const CardSkeleton = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="80%" height={24} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={40} />
    </CardContent>
  </Card>
);